/**
 * The chart behind "Chart ->": the roster column, the resident's head, the Stop and Watch
 * banner, the writes, the KPIs and the Overview tab. Ported from the design's renderVals
 * (L36967-37039 and L38389-39400) and fallRisk() (L33278); every input is a route answer.
 */
import type {
  ConditionSignalResponse,
  ConversationsResponse,
  CognitiveResponse,
  DirectivesResponse,
  EhrStatusResponse,
  FacesheetResponse,
  FallRiskResult,
  FamilySharingResponse,
  GuardianSignal,
  MedicationsResponse,
  MedsTodayResponse,
  MemoriesResponse,
  MobilityInterventionsResponse,
  RemindersResponse,
  ResidentDetailResponse,
  VitalsResponse,
  AdlResponse,
  WatchMovementResponse,
  WatchRow,
} from '@aithora/shared';
import { pre } from './dom';
import type { ResidentsPersona } from './common';

type Ev = { preventDefault?: () => void } | undefined;
export const DASH = '–';
/** What a section the server refuses this viewer says (never 0 or empty). */
export const WITHHELD = 'Withheld for your role';
export interface ChartWithheld { facesheet?: boolean; meds?: boolean; fall?: boolean; vitals?: boolean; adl?: boolean }
const RED = '#B3261E', BLU = '#2F5C97', PUR = '#5B6B7C', SLT = '#54606C';
const MONO = 'font-variant-numeric:tabular-nums;';
/** The design's 18px tag (tag / tg / tg3). */
export const tag = (c: string, bg: string) =>
  'display:inline-flex;align-items:center;height:18px;padding:0 7px;border-radius:5px;' + MONO + 'font-size:14px;letter-spacing:.07em;font-weight:700;white-space:nowrap;color:' + c + ';background:' + bg + ';';

export const DAY = 86400000;
/** "5d ago", "14h ago", "40m ago", "today". */
export function ago(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return DASH;
  const ms = nowMs - Date.parse(iso);
  if (ms < 3600000) return Math.max(1, Math.round(ms / 60000)) + 'm ago';
  if (ms < DAY) return Math.round(ms / 3600000) + 'h ago';
  return Math.round(ms / DAY) + 'd ago';
}
const avg = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : NaN);

// ── fallRisk(): tier and colours from the score (design L33295) ──
export function tierOf(score: number | null | undefined): { tier: string; color: string; bg: string } {
  const s = score ?? 0;
  const tier = s >= 66 ? 'HIGH' : s >= 40 ? 'ELEVATED' : 'WATCH';
  return {
    tier,
    color: tier === 'HIGH' ? '#8C3A33' : tier === 'ELEVATED' ? '#B3261E' : '#5B6B7C',
    bg: tier === 'HIGH' ? 'rgba(140,58,51,.14)' : tier === 'ELEVATED' ? 'rgba(179,38,30,.12)' : 'rgba(91,107,124,.14)',
  };
}

/** The resident's mood, the way the design reads it off one 14-day series. */
export function moodOf(detail: ResidentDetailResponse | null, row: WatchRow | null, nowMs: number) {
  const s14 = detail?.moodSeries14?.filter((v): v is number => v != null) ?? null;
  let series: number[] | null = s14 && s14.length ? s14 : null;
  if (!series && detail) {
    // Without the daily series: the recorded moods, oldest first, within 14 days.
    series = detail.moods
      .filter((m) => m.score != null && nowMs - Date.parse(m.created_at) <= 14 * DAY)
      .map((m) => m.score as number)
      .reverse();
    if (!series.length) series = null;
  }
  if (!series) {
    const t = row?.moodToday;
    return { today: t == null ? null : t, mood7: null as number | null, mood14: null as number | null, below3: 0, trend: DASH };
  }
  const n = series.length;
  const last7 = series.slice(Math.max(0, n - 7));
  const prior = series.slice(Math.max(0, n - 14), Math.max(0, n - 7));
  const m7 = avg(last7);
  const mPrior = prior.length ? avg(prior) : m7;
  return {
    today: series[n - 1],
    mood7: m7,
    mood14: avg(series),
    below3: series.filter((v) => v < 3).length,
    trend: (m7 >= mPrior ? 'up ' : 'down ') + Math.abs(m7 - mPrior).toFixed(1) + ' on prior week',
  };
}

export interface ChartData {
  watch: WatchMovementResponse | null;
  rows: WatchRow[];
  row: WatchRow | null;
  detail: ResidentDetailResponse | null;
  facesheet: FacesheetResponse | null;
  fall: FallRiskResult | null;
  interventions: MobilityInterventionsResponse | null;
  cognitive: CognitiveResponse | null;
  meds: MedicationsResponse | null;
  medsToday: MedsTodayResponse | null;
  memories: MemoriesResponse | null;
  directives: DirectivesResponse | null;
  conversations: ConversationsResponse | null;
  reminders: RemindersResponse | null;
  sharing: FamilySharingResponse | null;
  ehr: EhrStatusResponse | null;
  signals: GuardianSignal[] | null;
  vitals: VitalsResponse | null;
  adl: AdlResponse | null;
  condition: ConditionSignalResponse | null;
}

export type ComposeMode = '' | 'note' | 'transfer' | 'escalate' | 'task';
export interface ChartWrite {
  kind: string;
  text: string;
  meta: string;
}
export interface ChartUi {
  filter: 'All' | 'Alerts' | 'Declining';
  compose: ComposeMode;
  draft: string;
  writes: ChartWrite[];
  breakGlass: boolean;
  sbarOpen: boolean;
  sbarText: Record<string, string>;
  carePath: string;
  methodOpen: boolean;
  logNote: string;
  directive: string;
  directiveToday: boolean;
  signalOutcome: Record<string, { outcome: string; correct: boolean | null; at: string }>;
  /** V9 "summary first": the Overview opens on the Today line; Show details opens the rest. */
  detailOpen?: boolean;
}

export interface ChartActs {
  setUi: (p: Partial<ChartUi>) => void;
  select: (id: string) => void;
  clearFocus: () => void;
  back: () => void;
  setTab: (t: 'overview' | 'vitals' | 'measures' | 'chart') => void;
  save: (mode: ComposeMode, text: string) => void;
  breakGlass: () => void;
  conditionAck: () => void;
  conditionClose: () => void;
  sbarSend: (fields: Record<string, string>, carePath: string) => void;
  /** assisted: an Assisted outcome (taken) on an order set to Assistance. */
  medGiven: (medicationId: string, dueAt: string, assisted?: boolean) => void;
  logIntervention: (note: string) => void;
  closeSignal: (id: string, outcome: string) => void;
  signalFeedback: (id: string, correct: boolean) => void;
  addDirective: (text: string, today: boolean) => void;
  goIntegrations: () => void;
  /** Go to a console path (an alert's next step). */
  openLink: (to: string) => void;
}

export interface ChartInput {
  persona: ResidentsPersona;
  readOnly: boolean;
  /** Sections the server refused this viewer. */
  withheld?: ChartWithheld;
  viewerName: string | null;
  data: ChartData;
  selId: string;
  focusMissing: string | null;
  tab: 'overview' | 'vitals' | 'measures' | 'chart';
  query: string;
  setQuery: (v: string) => void;
  ui: ChartUi;
  act: ChartActs;
  nowMs: number;
}

const ALERT_SEV: Record<string, [string, string, string, string]> = {
  critical: ['Critical', '#B3261E', 'rgba(179,38,30,.09)', 'rgba(179,38,30,.28)'],
  high: ['Warning', '#5B6B7C', 'rgba(91,107,124,.09)', 'rgba(91,107,124,.28)'],
  warning: ['Warning', '#5B6B7C', 'rgba(91,107,124,.09)', 'rgba(91,107,124,.28)'],
  medium: ['Warning', '#5B6B7C', 'rgba(91,107,124,.09)', 'rgba(91,107,124,.28)'],
  review: ['Review', '#2F5C97', 'rgba(79,138,214,.08)', 'rgba(79,138,214,.26)'],
  low: ['Review', '#2F5C97', 'rgba(79,138,214,.08)', 'rgba(79,138,214,.26)'],
};

const MED_PILL = (st: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.05em;border-radius:4px;padding:2px 7px;flex:0 0 auto;' +
  (st === 'GIVEN' ? 'color:#1F6B52;background:rgba(47,138,111,.14);' : st === 'MISSED' ? 'color:#B3261E;background:rgba(168,82,75,.14);' : 'color:#54606C;background:rgba(34,39,46,.07);');

const MEM_GROUPS: Array<[string, string[]]> = [
  ['People', ['person', 'people', 'family', 'friend']],
  ['Places & history', ['place', 'places', 'history', 'event', 'location']],
  ['Interests', ['interest', 'interests', 'hobby', 'topic', 'activity']],
  ['Rhythm & routine', ['routine', 'rhythm', 'habit', 'schedule']],
  ['Care facts', ['care_fact', 'care', 'health', 'preference', 'fact']],
];

const INTERVENTION_LABEL: Record<string, string> = {
  increased_checkins: 'More frequent check-ins',
  pt_referral: 'PT referral',
  night_safety: 'Night safety measures',
  med_review: 'Medication review',
  family_visit: 'Family visit',
  other: 'Action logged',
};

const clock = (iso: string) => {
  const d = new Date(iso);
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
};

export function chartVals(inp: ChartInput) {
  const { data, ui, act, nowMs, persona } = inp;
  const wh = inp.withheld ?? {};
  const rows = data.rows;
  const row = data.row;
  const home = data.watch?.homeFacilityName ?? null;
  const masked = !!row?.masked;
  const name = row?.name ?? data.facesheet?.facesheet.name ?? DASH;
  const first = masked ? name : String(name).split(' ')[0];
  const room = masked ? DASH : row?.room ?? data.facesheet?.facesheet.room ?? DASH;
  const age = masked ? DASH : row?.age != null ? String(row.age) : data.facesheet?.facesheet.age != null ? String(data.facesheet.facesheet.age) : DASH;
  const facility = row?.facilityName ?? data.facesheet?.facesheet.facilityName ?? home ?? DASH;
  const q = inp.query.trim().toLowerCase();

  // ── roster column (design resRoster, L36969) ──
  const triage = (r: WatchRow) => (r.mobilityScore ?? 0) * 100 + (r.openAlerts ?? 0) * 10 + (r.declining ? 5 : 0);
  const roster = rows
    .filter((r) => {
      if (q && String(r.name ?? '').toLowerCase().indexOf(q) < 0) return false;
      if (ui.filter === 'Alerts') return (r.openAlerts ?? 0) > 0;
      if (ui.filter === 'Declining') return r.declining;
      return true;
    })
    .sort((a, b) => triage(b) - triage(a) || String(a.name).localeCompare(String(b.name)))
    .map((r, ri) => {
      const f = tierOf(r.mobilityScore);
      return {
        name: r.name ?? DASH,
        facility: r.facilityName ?? '',
        initial: String(r.name ?? '').slice(0, 1),
        meta: (r.masked ? (r.facilityName ?? DASH) + ' · initials only' : 'Age ' + (r.age ?? DASH) + ' · Room ' + (r.room ?? DASH)) + ' · ' + ((r.devices ?? []).join(', ') || DASH),
        meta2: 'mobility ' + (r.mobilityScore ?? DASH) + ' · mood ' + (r.moodToday == null ? DASH : r.moodToday.toFixed(1)),
        statusChip: f.tier,
        statusChipStyle: 'font-size:14px;font-weight:700;letter-spacing:.05em;border-radius:4px;padding:1px 5px;flex:0 0 auto;color:' + f.color + ';background:' + f.bg + ';',
        onClick: (e: Ev) => { pre(e); act.select(r.residentId); },
        avatarStyle: 'display:flex;align-items:center;justify-content:center;width:30px;height:30px;flex:0 0 30px;border-radius:8px;background:#2F4A6B;color:#fff;font-size:14px;font-weight:700;',
        alertCount: String(r.openAlerts ?? 0),
        dotStyle: (r.openAlerts ?? 0) > 0
          ? 'display:inline-flex;align-items:center;justify-content:center;min-width:16px;height:16px;padding:0 4px;border-radius:8px;background:rgba(168,82,75,.14);color:#B3261E;font-size:14px;font-weight:700;flex:0 0 auto;white-space:nowrap;'
          : 'display:none;',
        style:
          'animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(300, ri * 24) + 'ms;' +
          'display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:9px 11px;border-radius:9px;text-decoration:none;color:#22272E;' +
          (inp.selId === r.residentId ? 'background:rgba(79,138,214,.10);border:1px solid rgba(79,138,214,.35);' : 'border:1px solid transparent;'),
      };
    });

  const census = data.watch?.censusInScope ?? rows.length;
  const sample = data.watch && !data.watch.real ? ': SAMPLE, not the full census.' : '.';
  const scopeNote = persona === 'Operator'
    ? 'Fleet scope · ' + census + ' residents across ' + (data.watch?.facilitiesInScope ?? new Set(rows.map((r) => r.facilityName)).size) + ' facilities. Showing the ' + rows.length +
      ' ranked highest for attention' + sample + (rows.some((r) => r.masked)
        ? ' Residents outside ' + (home ?? 'your building') + ' show as initials: minimum necessary, not a permission difference.'
        : ' Names are shown read only, and each view is logged.')
    : persona === 'Care Staff'
      ? 'Your shift · ' + rows.length + ' residents assigned to you at ' + (home ?? 'your building') + '. Other residents in this building are not yours to open.'
      : (home ?? 'Your building') + ' · ' + census + ' residents in your building. Showing the ' + rows.length + ' ranked highest for attention' + sample + ' Other facilities are out of your scope.';

  // ── mood, cognition, meds, contact ──
  const mood = moodOf(data.detail, row, nowMs);
  const cogAll = (data.cognitive?.assessments ?? data.detail?.cognitive ?? []) as Array<{ assessment_type: string | null; score: number | null; created_at: string }>;
  const latestOf = (t: string) => cogAll.filter((c) => String(c.assessment_type ?? '').toUpperCase() === t && c.score != null).sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0] ?? null;
  const mmse = latestOf('MMSE');
  const moca = latestOf('MOCA');
  const cog = moca ?? mmse;
  const cogInstrument = moca ? 'Cognition MoCA' : 'Cognition MMSE';
  const cogFoot = cog ? (moca ? 'MoCA' : 'MMSE') + ', administered ' + ago(cog.created_at, nowMs) : 'not administered';
  const medList = data.meds?.medications ?? [];
  const doses = data.medsToday?.doses ?? [];
  // A self-administered or third-party order is never due for staff.
  const medDue = doses.filter((d) => d.status === 'DUE' && d.serviceLevel !== 'self_administered' && d.serviceLevel !== 'third_party').length;
  const medGiven = doses.filter((d) => d.status === 'GIVEN').length;
  const medMissed = doses.filter((d) => d.status === 'MISSED');
  const convs = data.conversations?.conversations ?? [];
  const lastConv = convs.slice().sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0] ?? null;
  const convCount = data.detail?.conversationCount ?? convs.length;

  // ── the resident's status (design L35657) ──
  const declining = !!row?.declining;
  const openAlerts = row?.openAlerts ?? (data.detail?.alerts.length ?? 0);
  const status = declining ? 'Declining' : openAlerts > 0 ? 'Monitoring' : 'Stable';
  const statusColor = declining ? '#B3261E' : openAlerts > 0 ? '#5B6B7C' : '#1F6B52';
  const statusBg = declining ? 'rgba(179,38,30,.13)' : openAlerts > 0 ? 'rgba(91,107,124,.14)' : 'rgba(47,138,111,.13)';

  // ── fall risk (design fallRisk + rFall*) ──
  const fr = data.fall;
  const scored = !!fr && fr.state === 'scored' && fr.score != null;
  const fScore = scored ? (fr!.score as number) : row?.mobilityScore ?? null;
  const ft = tierOf(fScore);
  const fTrend = fr?.trend ?? DASH;
  const contribs = (fr?.topContributors ?? []).slice().sort((a, b) => b.points - a.points);
  const maxPts = Math.max(1, ...contribs.map((d) => d.points));
  const drivers = contribs.map((d) => ({
    k: d.label,
    z: (d.z >= 0 ? '+' : '−') + Math.abs(d.z).toFixed(1),
    pts: '+' + Math.round(d.points),
    why: d.explanation,
    w: Math.round((d.points / maxPts) * 100) + '%',
    color: d.points >= 13 ? '#9E4A42' : d.points >= 10 ? '#B87155' : d.points >= 6 ? '#CE9A6B' : '#E0C39A',
  }));
  const interventions = (data.interventions?.interventions ?? []).map((iv) => {
    const at = iv.scoreAtTime;
    const diff = at != null && fScore != null ? at - fScore : null;
    return {
      act: iv.note || INTERVENTION_LABEL[iv.interventionType] || 'Action logged',
      who: iv.createdByName ?? 'staff',
      when: ago(iv.createdAt, nowMs),
      delta: at != null && fScore != null ? 'score ' + at + ' at log, ' + fScore + ' today' : 'no score at log',
      dir: diff == null ? DASH : diff > 0 ? 'down ' + diff : diff < 0 ? 'up ' + -diff : 'no change',
    };
  });
  const asOfToday = fr?.asOfDay ? fr.asOfDay === new Date(nowMs).toISOString().slice(0, 10) || fr.asOfDay === localDay(nowMs) : false;

  // ── alert cards: the resident's open alerts (design rAlertCards) ──
  const alertCards = (data.detail?.alerts ?? []).map((a) => {
    const s = ALERT_SEV[String(a.severity).toLowerCase()] ?? ALERT_SEV.medium;
    const ext = a;
    // A daily indicator (mobility is recomputed each morning) is "today", not an hour count.
    const daily = /mobility/i.test(a.type) && localDay(Date.parse(a.created_at)) === localDay(nowMs);
    const to = ext.to ?? null;
    return {
      sev: s[0], title: a.title, age: daily ? 'today' : ago(a.created_at, nowMs), why: ext.why ?? '', next: ext.next && to ? '→ ' + ext.next : '', color: s[1], bg: s[2], bd: s[3],
      onNext: (e: Ev) => { pre(e); if (to) act.openLink(to); },
    };
  });

  // ── vitals and ADL, for the Stop and Watch reasons and the SBAR ──
  const vcards = data.vitals?.vitals.vitals ?? [];
  const vLatest = (m: string) => vcards.find((v) => v.metric === m)?.value ?? null;
  const sysLatest = vLatest('systolic_bp');
  const hrLatest = vLatest('heart_rate');
  const tempLatest = vLatest('body_temperature');
  const spoCard = vcards.find((v) => v.metric === 'oxygen_saturation');
  const spoLatest = spoCard?.value ?? null;
  const wt = data.vitals?.vitals.weight;
  const p30 = wt?.change30Pct ?? null;
  const p180 = wt?.change180Pct ?? null;
  const trip30 = p30 != null && p30 <= -5;
  const trip180 = p180 != null && p180 <= -10;
  const wTrip = trip30 || trip180;
  const pct = (v: number) => (v >= 0 ? '+' : '−') + Math.abs(v).toFixed(1) + '%';
  const adlDecline = (data.adl?.adl.levels ?? []).filter((l) => l.now > l.baseline).length;

  // ── Stop and Watch (design rg*, L38756) ──
  const reasons: Array<[string, string, string, number]> = [];
  if (scored && (ft.tier === 'HIGH' || ft.tier === 'ELEVATED')) reasons.push(['Mobility', ft.tier.toLowerCase() + ' · ' + fTrend, ft.color, ft.tier === 'HIGH' ? 2 : 1]);
  if (adlDecline > 0) reasons.push(['ADL', adlDecline + (adlDecline === 1 ? ' task' : ' tasks') + ' needing more help', PUR, adlDecline >= 2 ? 1 : 0]);
  if (wTrip) reasons.push(['Weight', pct((trip30 ? p30 : p180) as number) + (trip30 ? ' in 30d' : ' in 180d'), RED, 2]);
  if (spoLatest != null && spoLatest < 92) reasons.push(['SpO₂', Math.round(spoLatest) + '%', RED, 2]);
  if (tempLatest != null && tempLatest > 99.5) reasons.push(['Temperature', tempLatest.toFixed(1) + '°F', RED, 1]);
  if (hrLatest != null && hrLatest > 100) reasons.push(['Heart rate', Math.round(hrLatest) + ' bpm', RED, 1]);
  const cond = data.condition;
  const off = !!cond?.closedAt;
  const acked = !!cond?.acknowledged;
  const sent = !!cond?.sbarSentAt;
  const shown = !off && reasons.length > 0;
  const worst = reasons.reduce((m, r) => Math.max(m, r[3]), 0);
  const tier = worst === 2 ? 'HIGH' : worst === 1 ? 'ELEVATED' : 'WATCH';
  const tierColor = tier === 'HIGH' ? '#8C3A33' : tier === 'ELEVATED' ? '#B3261E' : '#5B6B7C';
  const flagged = cond?.firstFlaggedAt ? Math.max(0, nowMs - Date.parse(cond.firstFlaggedAt)) : null;
  const flaggedTxt = flagged == null ? DASH : Math.floor(flagged / 3600000) + 'h ' + Math.floor((flagged % 3600000) / 60000) + 'm';
  const low = (s: string) => (s === 'ADL' || s === 'SpO₂' ? s : s.toLowerCase());
  const plainWhy = reasons.map((r) => low(r[0])).join(', ');
  const headline = 'Check ' + first + ': ' + (reasons.length > 1
    ? 'more than one thing has moved off ' + first + '’s own baseline (' + plainWhy + ').'
    : 'their ' + plainWhy + ' has moved off their own baseline.');
  const situation = reasons.length > 1 ? 'More than one thing has moved off their own baseline (' + plainWhy + ').' : 'Their ' + plainWhy + ' has moved off their own baseline.';
  const ranAt = cond?.detectorsRanAt ? clock(cond.detectorsRanAt) : null;
  const taStyle = 'box-sizing:border-box;width:100%;min-height:62px;margin-top:5px;padding:9px 11px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FCFAF4;font-family:inherit;font-size:16px;line-height:1.5;color:#22272E;resize:vertical;';
  const spoStale = spoCard?.staleHours ? 'stale ' + spoCard.staleHours + 'h' : spoLatest == null ? 'not assessed' : Math.round(spoLatest) + '%';
  const vitalLine = 'BP ' + (sysLatest == null ? DASH : Math.round(sysLatest)) + ' systolic, HR ' + (hrLatest == null ? DASH : Math.round(hrLatest)) + ', temp ' + (tempLatest == null ? DASH : tempLatest.toFixed(1)) + '°F, SpO₂ ' + spoStale;
  const sbarDefaults: Array<[string, string]> = [
    ['Situation', name + ', room ' + room + '. ' + situation + ' First flagged ' + flaggedTxt + ' ago.'],
    ['Background', 'Age ' + age + '. ' + medList.length + ' active medications. Mood ' + (mood.today == null ? DASH : mood.today.toFixed(1)) + '/10 today, ' + (mood.mood14 == null ? DASH : mood.mood14.toFixed(1)) + ' average over 14 days.'],
    ['Assessment', vitalLine + '. Mobility risk ' + ft.tier.toLowerCase() + ', ' + fTrend + '. ' + (wTrip ? 'Weight ' + pct((trip30 ? p30 : p180) as number) + (trip30 ? ' over 30 days.' : ' over 180 days.') : 'Weight within threshold.')],
    ['Recommendation', 'Requesting a provider assessment today. Please advise on orders, labs, or whether to observe in place.'],
  ];
  const sbarVal = (k: string, d: string) => (ui.sbarText[k] !== undefined ? ui.sbarText[k] : d);
  const PATHS = ['UTI', 'Lower respiratory illness', 'Fever', 'GI symptoms', 'Mental status change', 'Dehydration'];
  const path = cond?.carePath ?? ui.carePath;

  // ── actions and writes (design L39093) ──
  const canWrite = !inp.readOnly;
  const isCare = persona === 'Care Staff';
  const MODES: Record<Exclude<ComposeMode, ''>, [string, string, string, string, string]> = {
    note: ['Progress note', 'writes to the timeline', 'What was observed, and what was done.', 'Save note', 'NOTE'],
    transfer: ['Log an acute-care transfer', 'writes to transfers and the timeline', 'Where they went, why, and what was tried first.', 'Log transfer', 'TRANSFER'],
    escalate: ['Escalate to the on-call provider', 'pages the ladder and writes to the ledger', 'What you need them to decide, and by when.', 'Send escalation', 'ESCALATION'],
    task: ['Order a care task', 'writes to the shift task list', 'What needs doing, and by when.', 'Order task', 'TASK'],
  };
  const mode = ui.compose;
  const M = mode ? MODES[mode] : ['', '', '', '', ''];
  const actBtn = (primary: boolean) =>
    'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (primary ? 'background:#2F5C97;color:#fff;' : 'border:1px solid rgba(34,39,46,.2);color:#3C454F;');
  const openMode = (k: ComposeMode) => (e: Ev) => { pre(e); act.setUi({ compose: mode === k ? '' : k }); };
  const acts: Array<{ label: string; onClick: (e: Ev) => void; style: string }> = [];
  if (canWrite) {
    acts.push({ label: 'Add note', onClick: openMode('note'), style: actBtn(mode === 'note') });
    acts.push({ label: 'Order care task', onClick: openMode('task'), style: actBtn(mode === 'task') });
    acts.push({ label: 'Escalate', onClick: openMode('escalate'), style: actBtn(mode === 'escalate') });
    if (!isCare) acts.push({ label: 'Log transfer', onClick: openMode('transfer'), style: actBtn(mode === 'transfer') });
    acts.push({
      label: ui.breakGlass ? 'Break-glass active' : 'Declare break-glass',
      onClick: (e: Ev) => { pre(e); if (!ui.breakGlass) act.breakGlass(); },
      style:
        'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (ui.breakGlass ? 'color:#B3261E;background:rgba(179,38,30,.1);pointer-events:none;' : 'border:1px solid rgba(179,38,30,.35);color:#B3261E;'),
    });
  }
  const draft = ui.draft;

  // ── medications and the allergy cross-check (design L39145) ──
  const fs = data.facesheet?.facesheet ?? null;
  const allergyState = fs?.allergyState ?? (fs ? (fs.allergies.length ? 'recorded' : 'not_assessed') : 'not_assessed');
  const algKnown = allergyState !== 'not_assessed';
  const algNone = allergyState === 'affirmed_none';
  const algTxt = algNone ? 'NKDA, confirmed' : (fs?.allergies ?? []).join(', ');
  const CLASS: Array<[string, string]> = [['cillin', 'Penicillin'], ['amox', 'Penicillin'], ['sulfa', 'sulfa'], ['bactrim', 'sulfa'], ['iodine', 'iodine']];
  let conflicts = 0;
  const medRows = medList.map((m) => {
    const dose = doses.find((d) => d.medicationId === m.id) ?? null;
    const stt = dose?.status === 'UPCOMING' ? 'DUE' : dose?.status ?? 'DUE';
    // How the order is given: an Assistance order records Assisted; a self-administered or
    // third-party order has nothing for staff to record. An assisted record reads as such
    // (the MAR is Florida's daily medication observation record, 59A-36.008(5)(b)).
    const level = dose?.serviceLevel ?? m.service_level ?? 'administer';
    const offPass = level === 'self_administered' || level === 'third_party';
    const shown = level === 'self_administered' ? 'ON THEIR OWN' : level === 'third_party' ? 'THIRD PARTY'
      : dose?.assisted ? 'ASSISTED · ' + (stt === 'GIVEN' ? 'TAKEN' : stt) : stt;
    const hit = algKnown && !algNone ? CLASS.filter((c) => m.name.toLowerCase().indexOf(c[0]) >= 0 && algTxt.toLowerCase().indexOf(c[1].toLowerCase()) >= 0)[0] : null;
    if (hit) conflicts += 1;
    const due = !offPass && (stt === 'DUE' || stt === 'MISSED');
    const done = stt === 'GIVEN' || offPass;
    return {
      time: dose ? clock(dose.dueAt) : DASH,
      timeColor: offPass ? SLT : stt === 'MISSED' ? RED : stt === 'DUE' ? PUR : SLT,
      name: m.name,
      dose: m.dosage ?? '',
      freq: m.frequency ?? '',
      route: dose?.route ?? DASH,
      status: shown,
      pill: MED_PILL(offPass ? '' : stt),
      conflict: !!hit,
      conflictNote: hit ? 'Allergy cross-check: documented ' + hit[1] + ' allergy. Confirm before administering.' : '',
      act: offPass ? 'Nothing to record' : done ? 'Recorded' : !canWrite ? 'Read-only' : level === 'assistance' ? 'Record assisted' : 'Record given',
      actStyle:
        'display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (done || !canWrite ? 'color:#54606C;background:rgba(34,39,46,.06);pointer-events:none;' : hit ? 'background:#B3261E;color:#fff;' : 'background:#2F5C97;color:#fff;'),
      onAct: (e: Ev) => { pre(e); if (done || !canWrite || !dose) return; act.medGiven(m.id, dose.dueAt, level === 'assistance'); },
      due,
      order: dose ? Date.parse(dose.dueAt) : 0,
    };
  });
  const meds = medRows.sort((a, b) => (a.due === b.due ? 0 : a.due ? -1 : 1));

  // ── risk signals (Guardian, abuse and neglect) ──
  const OUTCOMES: Array<[string, string, string]> = [['resolved', '#1F6B52', 'rgba(47,138,111,.16)'], ['escalated', '#B3261E', 'rgba(179,38,30,.14)'], ['no action needed', '#54606C', 'rgba(34,39,46,.08)']];
  const riskSignals = (data.signals ?? [])
    .filter((s) => s.residentId === inp.selId && (s.kind === 'abuse_neglect' || s.kind === 'scam') && !s.closedAt)
    .map((s) => {
      const rec = ui.signalOutcome[s.id] ?? null;
      return {
        k: s.label ?? (s.kind === 'scam' ? 'Possible scam contact' : 'Abuse or neglect signal'),
        note: s.note ?? 'surfaced ' + ago(s.surfacedAt, nowMs),
        sev: s.severity ?? 'Open',
        color: PUR,
        isOpen: !rec && canWrite,
        closed: !!rec,
        outcomes: OUTCOMES.map((o) => ({
          label: o[0],
          onClick: (e: Ev) => { pre(e); act.closeSignal(s.id, o[0]); },
          style: 'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;text-decoration:none;white-space:nowrap;font-weight:600;color:' + o[1] + ';background:' + o[2] + ';border:1px solid ' + o[1] + '40;',
        })),
        outLabel: rec ? rec.outcome : '',
        outStyle: rec
          ? 'display:inline-flex;align-items:center;height:20px;padding:0 9px;border-radius:5px;font-size:14px;letter-spacing:.06em;font-weight:700;white-space:nowrap;color:' +
            (rec.outcome === 'resolved' ? '#1F6B52;background:rgba(47,138,111,.16);' : rec.outcome === 'escalated' ? '#B3261E;background:rgba(179,38,30,.14);' : '#54606C;background:rgba(34,39,46,.08);')
          : '',
        actor: rec ? (inp.viewerName ?? 'You') + ' · ' + rec.at : '',
        correctAsked: !!rec && rec.correct === null && canWrite,
        correctAnswered: !!rec && rec.correct !== null,
        correctLabel: rec && rec.correct === true ? 'flagged correct' : rec && rec.correct === false ? 'flagged a false positive' : '',
        correctStyle: 'font-size:14px;font-weight:600;white-space:nowrap;color:' + (rec && rec.correct === false ? '#B3261E' : '#1F6B52') + ';',
        markCorrect: (e: Ev) => { pre(e); act.signalFeedback(s.id, true); },
        markWrong: (e: Ev) => { pre(e); act.signalFeedback(s.id, false); },
        yesStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:#1F6B52;background:rgba(47,138,111,.14);border:1px solid rgba(47,138,111,.34);',
        noStyle: 'display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.1);border:1px solid rgba(179,38,30,.34);',
      };
    });

  // ── memory graph ──
  const ents = data.memories?.entities ?? [];
  const memoryGroups = MEM_GROUPS.map(([k, types]) => {
    const items = ents.filter((e) => types.includes(String(e.entity_type).toLowerCase())).map((e) => e.canonical_name ?? e.redacted_label ?? '').filter(Boolean);
    return { k, items, empty: items.length === 0, emptyNote: 'nothing learned yet' };
  });

  // ── mood bars: the recorded moods, newest first ──
  const moodBars = (data.detail?.moods ?? []).filter((m) => m.score != null).slice(0, 14).map((m) => {
    const s = m.score as number;
    return { w: Math.round(Math.max(0, Math.min(10, s)) * 10) + '%', c: s >= 9.5 ? '#D08A3E' : s >= 7 ? '#D0913F' : '#C4685F' };
  });

  // ── reminders (design remVals rmResidentList) ──
  const KINDC: Record<string, string> = { Medication: '#B3261E', Appointment: '#2F5C97', 'Check-in': '#1F6B52', Custom: '#54606C' };
  const TIER: Record<string, [string, string]> = {
    Medication: ['#B3261E', 'rides the medication-missed alert'],
    Appointment: ['#5B6B7C', 'no escalation'],
    'Check-in': ['#5B6B7C', 'no escalation'],
    Custom: ['#54606C', 'a to-do, not a safety signal'],
  };
  const STATEC: Record<string, [string, string, string]> = {
    active: ['ON', '#1F6B52', 'rgba(47,138,111,.13)'],
    missed: ['MISSED · ESCALATED', '#B3261E', 'rgba(179,38,30,.13)'],
    done: ['DONE', '#54606C', 'rgba(34,39,46,.08)'],
  };
  const kindName = (k: string) => (k === 'medication' ? 'Medication' : k === 'appointment' ? 'Appointment' : k === 'checkin' ? 'Check-in' : 'Custom');
  const rems = (data.reminders?.reminders ?? []).filter((r) => r.status !== 'cancelled').map((r) => {
    const kind = kindName(String(r.kind));
    const state = r.missed ? 'missed' : r.status === 'done' ? 'done' : 'active';
    const when = r.nextFireAt ?? r.fireAt;
    // Daily: the time. Weekly on one day, or a one-off: the day first ("Fri 10:30 am"), as the design does.
    const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const tz = r.timezone || undefined;
    const time = r.recurrence?.times?.[0]
      ? (r.recurrence.freq === 'weekly' && r.recurrence.daysOfWeek?.length === 1 ? WD[r.recurrence.daysOfWeek[0]] + ' ' : '') + timeOfDay(r.recurrence.times[0])
      : when
        ? new Date(when).toLocaleString('en-US', { weekday: 'short', timeZone: tz }) + ' ' + new Date(when).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).toLowerCase()
        : DASH;
    return {
      kind,
      text: r.title,
      time,
      author: 'set by ' + (r.createdByName ?? 'the care team'),
      kindStyle: 'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-size:14px;letter-spacing:.07em;text-transform:uppercase;font-weight:700;color:#fff;background:' + KINDC[kind] + ';',
      stateLabel: STATEC[state][0],
      stateStyle: 'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.07em;font-weight:700;color:' + STATEC[state][1] + ';background:' + STATEC[state][2] + ';',
      tierColor: TIER[kind][0],
      tierNote: TIER[kind][1],
      state,
    };
  });

  const sharingOn = data.sharing?.sharing ?? null;
  const cardLabel = (d: typeof lastConv) => (d ? d.preview || d.title : '');

  return {
    rwChart: true,
    rwBack: (e: Ev) => { pre(e); act.back(); },
    rQuery: inp.query,
    onRQuery: (e: { target: { value: string } }) => inp.setQuery(e.target.value),
    rFilters: (['All', 'Alerts', 'Declining'] as const).map((f) => ({
      label: f,
      onClick: (e: Ev) => { pre(e); act.setUi({ filter: f }); },
      style:
        'display:inline-flex;align-items:center;justify-content:center;flex:1;height:30px;border-radius:7px;font-size:14px;text-decoration:none;box-sizing:border-box;white-space:nowrap;' +
        (ui.filter === f ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.14);color:#54606C;'),
    })),
    rScopeNote: scopeNote,
    rRoster: roster,
    rdTabs: (['overview', 'vitals', 'measures', 'chart'] as const).map((id) => ({
      label: id === 'overview' ? 'Overview' : id === 'vitals' ? 'Vitals & ADL' : id === 'measures' ? 'Measures' : 'Chart',
      onClick: (e: Ev) => { pre(e); act.setTab(id); },
      style:
        'display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
        (inp.tab === id ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.14);'),
    })),
    rdIsOverview: inp.tab === 'overview',
    rdIsMeasures: inp.tab === 'measures',
    rdIsVitals: inp.tab === 'vitals',
    rdIsChart: inp.tab === 'chart',
    rdFocusMissing: !!inp.focusMissing,
    rdFocusNote: inp.focusMissing ? 'That resident is not in this roster, so their record is not shown here. Showing ' + name + '.' : '',
    rdFocusClear: (e: Ev) => { pre(e); act.clearFocus(); },
    rSel: name,
    rSelInitial: String(name).slice(0, 1),
    rSelFacility: facility + (masked ? ' · initials only, minimum necessary' : ''),
    rSelStatus: status,
    rSelStatusStyle: 'display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:600;border-radius:20px;padding:3px 10px;color:' + statusColor + ';background:' + statusBg + ';',
    rSelAge: age,
    rSelRoom: room,
    rMoodToday: mood.today == null ? DASH : mood.today.toFixed(1),
    rKpis: [
      { k: 'Mood 7d', v: mood.mood7 == null ? DASH : mood.mood7.toFixed(1), foot: mood.trend, color: mood.mood7 != null && mood.mood7 < 3.5 ? '#5B6B7C' : '#22272E' },
      { k: cogInstrument, v: cog ? String(cog.score) + ' /30' : DASH, foot: cogFoot, color: '#22272E' },
      wh.meds
        ? { k: 'Medications', v: DASH, foot: WITHHELD, color: '#54606C' }
        : { k: 'Medications', v: String(medList.length), foot: medDue + ' due today · ' + medGiven + ' given', color: medList.length >= 10 ? '#5B6B7C' : '#22272E' },
      { k: 'Last contact', v: lastConv ? ago(lastConv.startedAt, nowMs) : DASH, foot: convCount + (convCount === 1 ? ' conversation' : ' conversations'), color: '#22272E' },
    ],

    // Stop and Watch banner
    rgShown: shown,
    rgNone: !shown,
    rgNoneNote: off
      ? 'Signal closed as no change on assessment. The detectors keep running.'
      : 'No change-in-condition signal for ' + first + '.' + (ranAt ? ' Detectors last ran today at ' + ranAt + ' and found nothing above threshold.' : ''),
    rgWrapStyle: 'border-top:2px solid ' + tierColor + ';padding:13px 16px 15px;background:' + (tier === 'HIGH' ? 'rgba(179,38,30,.04)' : 'rgba(34,39,46,.02)') + ';',
    rgKindStyle: tag(tierColor, tier === 'HIGH' ? 'rgba(179,38,30,.12)' : 'rgba(47,92,151,.12)'),
    rgTier: tier,
    rgTierStyle: tag(tierColor, tier === 'HIGH' ? 'rgba(140,58,51,.14)' : tier === 'ELEVATED' ? 'rgba(179,38,30,.12)' : 'rgba(91,107,124,.14)'),
    rgElapsed: reasons.length + (reasons.length === 1 ? ' signal moved' : ' signals moved') + ' · first flagged ' + flaggedTxt + ' ago',
    rgHeadline: headline,
    rgBody: 'This is an observation drawn from ' + first + '’s own trend, not a diagnosis and not a probability of a named event. A licensed nurse assesses and decides what happens next.',
    rgEvidence: reasons.map((r) => ({ k: r[0], v: r[1], color: r[2], dot: 'width:6px;height:6px;border-radius:50%;flex:0 0 6px;background:' + r[2] + ';' })),
    rgProvenance: 'Derived by the mobility and vitals detectors from ambient activity, bed-exit, documented ADLs and recorded weights' + (ranAt ? ' · recomputed today ' + ranAt : '') + '. Model-derived, not clinician-recorded.',
    rgUnacked: shown && !acked,
    rgAcked: shown && acked,
    rgAckNote: 'A Stop and Watch is a clinical alert. The licensed nurse acknowledges receipt in writing, assesses the resident, and only then decides whether to raise an SBAR to the provider.',
    rgAckedBy: 'Acknowledged in writing · ' + (cond?.acknowledged?.by ?? inp.viewerName ?? 'you') + ' · ' + (cond?.acknowledged ? ago(cond.acknowledged.at, nowMs).replace(/^1m ago$/, 'just now') : 'just now'),
    rgAck: (e: Ev) => { pre(e); act.conditionAck(); },
    rgClear: (e: Ev) => { pre(e); act.conditionClose(); },
    rgSbarToggle: (e: Ev) => { pre(e); act.setUi({ sbarOpen: !ui.sbarOpen }); },
    rgSbarBtn: ui.sbarOpen ? 'Hide SBAR' : sent ? 'View SBAR' : 'Start SBAR',
    // V9: read-only roles see the Stop and Watch, never Acknowledge, Start SBAR or the SBAR form.
    rgCanAct: canWrite,
    rgSbarOpen: shown && acked && ui.sbarOpen && canWrite,
    rgSbarFields: sbarDefaults.map(([k, d]) => ({
      k,
      val: sbarVal(k, d),
      onSet: (e: { target: { value: string } }) => act.setUi({ sbarText: { ...ui.sbarText, [k]: e.target.value } }),
      locked: sent,
      style: taStyle + (sent ? 'background:rgba(34,39,46,.04);color:#54606C;' : ''),
    })),
    rgSbarSend: (e: Ev) => {
      pre(e);
      if (sent) return;
      act.sbarSend(Object.fromEntries(sbarDefaults.map(([k, d]) => [k, sbarVal(k, d)])), path);
    },
    rgSbarSendLabel: sent ? 'Sent to provider' : 'Send SBAR to provider',
    rgSbarSendStyle:
      'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' +
      (sent ? 'background:rgba(47,138,111,.14);color:#1F6B52;pointer-events:none;' : 'background:#2F5C97;color:#fff;'),
    rgSbarNote: sent
      ? 'Sent ' + (path ? 'with the ' + path.toLowerCase() + ' care path attached. ' : '') + 'Written to the resident timeline and the ledger. The provider response lands back here.'
      : 'Sending writes the note to the timeline and the ledger. Nothing is auto-diagnosed and no order is placed.',
    rgPaths: PATHS.map((p) => ({
      label: p,
      onClick: (e: Ev) => { pre(e); act.setUi({ carePath: path === p ? '' : p }); },
      style:
        'display:inline-flex;align-items:center;min-height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
        (path === p ? 'background:#22384F;color:#fff;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;'),
    })),
    rgPathNote: path
      ? 'INTERACT ' + path.toLowerCase() + ' care path attached. It guides the assessment; it does not make the decision.'
      : 'Attach the care path that matches what the nurse found, so the assessment follows the same steps every time.',
    rgBreakGlass: ui.breakGlass,
    rgBreakGlassNote: 'Scoped read granted outside the normal assignment. This access is written to the audit trail and the ledger, and stays visible for as long as the grant is active.',

    // actions
    rActions: acts,
    rActionsReadOnly: !canWrite,
    rActionsReadOnlyNote: 'Read-only at your role: you see the chart and hold no setters.',
    rComposeOpen: !!mode,
    rComposeTitle: M[0],
    rComposeHint: M[1],
    rComposePlaceholder: M[2],
    rComposeText: draft,
    rComposeSet: (e: { target: { value: string } }) => act.setUi({ draft: e.target.value }),
    rComposeSave: (e: Ev) => { pre(e); if (!draft.trim() || !mode) return; act.save(mode, draft.trim()); },
    rComposeSaveLabel: M[3],
    rComposeSaveStyle:
      'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (draft.trim() ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.06);color:#54606C;pointer-events:none;'),
    rComposeCancel: (e: Ev) => { pre(e); act.setUi({ compose: '' }); },
    rComposeNote: 'Saving writes to this resident’s timeline and to the append-only ledger. Nothing is auto-completed on their behalf.',
    rHasWrites: ui.writes.length > 0,
    rWrites: ui.writes.slice().reverse().map((w) => ({
      ...w,
      kindStyle: tag(w.kind === 'BREAK-GLASS' || w.kind === 'ESCALATION' ? RED : BLU, w.kind === 'BREAK-GLASS' || w.kind === 'ESCALATION' ? 'rgba(179,38,30,.12)' : 'rgba(47,92,151,.12)'),
    })),

    // overview tab: the Today line first (V9), the rest behind Show details
    rTodayLine: 'Today: ' + (wh.meds ? '' : (medDue === 0 ? 'no meds due' : medDue + (medDue === 1 ? ' med due' : ' meds due')) + (medMissed.length ? ' (' + medMissed.length + ' missed)' : '') + ' · ') +
      (alertCards.length === 0 ? 'no alerts' : alertCards.length + (alertCards.length === 1 ? ' alert' : ' alerts')),
    rTodayWarn: medMissed.length > 0 || alertCards.length > 0,
    rTodayWhy: reasons.slice(0, 3).map((r) => ({ t: r[0], n: r[1] })),
    rTodayWhyHas: shown,
    rdDetail: !!ui.detailOpen,
    rdDetailLabel: ui.detailOpen ? 'Hide details' : 'Show details',
    rdDetailTog: (e: Ev) => { pre(e); act.setUi({ detailOpen: !ui.detailOpen }); },
    rAlertCards: alertCards,
    rAlertsEmpty: alertCards.length === 0,
    rFallScore: fScore == null ? DASH : String(fScore),
    rFallTier: ft.tier,
    rFallTierStyle: 'display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:5px;padding:3px 9px;color:' + ft.color + ';background:' + ft.bg + ';',
    rFallTrend: fTrend,
    rFallConf: wh.fall ? WITHHELD : 'confidence ' + (fr?.confidence ?? DASH),
    rFallAsOf: wh.fall ? WITHHELD : fr?.asOfDay ? (asOfToday ? 'updated today' : 'updated ' + fr.asOfDay) : 'not scored yet',
    rFallWindow: '~3-week actionable window',
    rFallFrame: wh.fall && !scored
      ? WITHHELD + ': the fall-risk trajectory is not shown to this role.'
      : scored
      ? 'Mobility risk ' + ft.tier.toLowerCase() + ' and ' + fTrend + ' against this resident’s own 28-day baseline. This is an indicator, not a probability of a named event.'
      : fr?.gate?.message ?? 'Not scored yet: the indicator needs its own baseline first.',
    rFallBarWidth: (fScore ?? 0) + '%',
    rFallBarColor: ft.color,
    rFallDrivers: drivers,
    rFallThreshold: 'Tiers: watch below 40, elevated 40 to 65, high 66 and above. Tunable per facility.',
    rFallMethod: 'Transparent weighted trend against the resident’s own baseline. Recomputed daily from ambient activity, bed-exit, Atlas conversation frequency and wearable signal.',
    rFallEvidence: 'Evidence basis: published in-home sensing studies (TigerPlace: a 2.5 cm/s decline in 7-day gait speed carried roughly 4.2x the odds of a fall within three weeks, ROC 0.86 in that cohort).',
    rFallAccuracy: 'Facility-specific accuracy: not yet established. No AUC or calibration curve is shown because this is not a trained model and no labeled fall outcomes exist yet. Accuracy accrues once outcomes are recorded.',
    rFallFreshness: 'Daily, off ambient and conversational signal. Not the quarterly MDS.',
    rInterventions: interventions,
    rInterventionEmpty: interventions.length === 0,
    rLogNote: ui.logNote,
    rLogSet: (e: { target: { value: string } }) => act.setUi({ logNote: e.target.value }),
    rLog: (e: Ev) => { pre(e); if (!ui.logNote.trim() || !canWrite) return; act.logIntervention(ui.logNote.trim()); },
    rMethodOpen: ui.methodOpen,
    rMethodCaret: ui.methodOpen ? '▴' : '▾',
    rMethodHint: ui.methodOpen ? '' : 'accuracy not yet established',
    rToggleMethod: (e: Ev) => { pre(e); act.setUi({ methodOpen: !ui.methodOpen }); },
    rRiskSignals: riskSignals,
    rRiskEmpty: riskSignals.length === 0,
    rRiskEmptyNote: 'No abuse or neglect signals on record for this resident.',
    rLoopNote: 'Closing a signal records the outcome, who closed it and when - then asks whether the alert was right. That last answer is the only thing that tunes the detector, and it is the difference between oversight and rubber-stamping.',
    rMoodBars: moodBars,
    rMoodAvg: 'avg ' + (mood.mood14 == null ? DASH : mood.mood14.toFixed(1)) + ' · today ' + (mood.today == null ? DASH : mood.today.toFixed(1)),
    rCogScore: mmse ? (mmse.score as number) : null,
    rMmseBand: mmse ? ((mmse.score as number) >= 24 ? 'Normal (24–30)' : (mmse.score as number) >= 19 ? 'Mild impairment (19–23)' : 'Moderate (10–18)') : 'not administered',
    rMmseBandColor: mmse && (mmse.score as number) >= 24 ? '#1F6B52' : '#5B6B7C',
    rMocaScore: moca ? (moca.score as number) : null,
    rMocaShown: !!moca,
    rMocaBand: moca ? ((moca.score as number) >= 26 ? 'Normal (26–30)' : 'Mild impairment (18–25)') : '',
    rMocaGated: !moca,
    rMocaNote: 'MoCA not administered. No score until a clean assessment exists.',
    rMemoryGroups: memoryGroups,
    rMedCount: wh.meds ? WITHHELD : medList.length + ' active',
    rMeds: wh.meds ? [] : meds,
    rMedAllergyText: wh.meds || wh.facesheet
      ? WITHHELD + ': the medication list and allergy cross-check are not shown to this role.'
      : !algKnown
      ? 'Allergies not yet assessed for this resident. The cross-check cannot run: confirm before any administration.'
      : conflicts > 0
        ? conflicts + (conflicts === 1 ? ' active medication matches' : ' active medications match') + ' the documented ' + algTxt + ' allergy. Confirm with the prescriber before administering.'
        : algNone
          ? 'No known drug allergies, confirmed. No conflict among the ' + medList.length + ' active medications.'
          : 'Documented allergies: ' + algTxt + '. No conflict among the ' + medList.length + ' active medications.',
    rMedAllergyStyle:
      'margin-top:9px;padding:9px 12px;border-radius:8px;font-size:14px;line-height:1.5;text-wrap:pretty;' +
      (!algKnown ? 'background:rgba(91,107,124,.1);border:1px solid rgba(91,107,124,.3);color:#4A5866;' : conflicts > 0 ? 'background:rgba(179,38,30,.07);border:1px solid rgba(179,38,30,.35);color:#8A1F19;' : 'background:rgba(34,39,46,.04);border:1px solid rgba(34,39,46,.1);color:#3C454F;'),
    rMedFooter: wh.meds ? WITHHELD + '.' :
      'Today: ' + medGiven + ' given, ' + medDue + ' due' + (medMissed.length ? ', ' + medMissed.length + ' missed' : '') +
      '. Right resident, drug, dose, route and time: the allergy cross-check runs on this list, and recording here writes to the MAR and the ledger.' +
      (medList.length >= 10 ? ' Polypharmacy: ' + medList.length + ' concurrent medications is at or above the facility review threshold of 10.' : ''),
    rDirectives: (data.directives?.directives ?? []).filter((d) => d.effectiveActive),
    rInstrEmpty: 'No instructions yet. Add one below. For example: “Remind her about PT at 2pm.”',
    rDirective: ui.directive,
    rDirectiveSet: (e: { target: { value: string } }) => act.setUi({ directive: e.target.value }),
    rDirectiveAdd: (e: Ev) => { pre(e); if (!ui.directive.trim() || !canWrite) return; act.addDirective(ui.directive.trim(), ui.directiveToday); },
    rDirectiveToday: ui.directiveToday,
    rDirectiveTodayToggle: (e: Ev) => { pre(e); act.setUi({ directiveToday: !ui.directiveToday }); },
    rmCount: rems.filter((r) => r.state === 'active').length + ' active · ' + rems.length + ' total',
    rmResidentList: rems.slice(0, 4),
    rConversations: convs.slice().sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt)).slice(0, 2).map((c) => ({ t: ago(c.startedAt, nowMs), text: cardLabel(c) })),
    rFamilyRows: ['Wellbeing status', 'Mood trend', 'Recent highlights', 'Life Story', 'Medical status'],
    rFamilyOn: sharingOn ? [sharingOn.wellbeing, sharingOn.moodTrend, sharingOn.highlights, sharingOn.lifeStory, sharingOn.medicalStatus] : [false, false, false, false, false],
    rFamilyCode: data.sharing?.familyCode ?? null,
    rLifeStorySub: name + '’s preserved life story. View & save as PDF for family',
    rFhirVendors: ['Epic', 'Oracle Health', 'athenahealth'],
    rFhirGate: data.ehr
      ? data.ehr.connected
        ? 'Connected to ' + (data.ehr.vendor ?? 'the EHR') + ' for ' + (data.ehr.facilityName ?? facility) + '.'
        : 'Disabled: no EHR connected for ' + (data.ehr.facilityName ?? facility) + '. Nothing can be pushed until a vendor endpoint is authorised.'
      : 'EHR connection status is not reported yet. Nothing can be pushed until a vendor endpoint is authorised.',
    rGoIntegrations: (e: Ev) => { pre(e); act.goIntegrations(); },
    rLifeStoryPdf: (e: Ev) => { pre(e); if (typeof window !== 'undefined') window.print(); },
    rCanWrite: canWrite,
  };
}

function localDay(ms: number): string {
  const d = new Date(ms);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** "20:00" -> "8:00 pm", the design's reminder times. */
function timeOfDay(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  return (h % 12 || 12) + ':' + String(m || 0).padStart(2, '0') + ' ' + ap;
}
